import { Face, FaceRetouchingOff } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import moment from 'moment/moment';
import React from 'react';
import { useSelector } from 'react-redux';

import { hasRight } from '#app/Common/UserCommon';
import DataGrid from '#app/Component/DataGrid';
import IconAction from '#app/Component/IconAction';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { StateType } from '#app/Store';
import { transformObjectToDataGridRowsArray } from '#app/Util/DataGridDataTransformer';
import { formatNumberToEurCurrency } from '#app/Util/NumberTool';
import useOrderClients from '#app/View/Sale/Client/SaleClient/hooks/useOrderClients';
import { Channel, ChannelTranslations } from '#app/typings/channel';
import { OrderClientListByClient, OrderClientState, OrderClientStateTranslation } from '#app/typings/orderClient';

const OrderClientList: React.FC = () => {
  const authenticationReducer = useSelector((state: StateType) => state.AuthenticationReducer);
  const { data: orderClients, totalItems, ...props } = useOrderClients();

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: 'Référence',
      minWidth: 180,
      sortable: false,
    },
    {
      field: 'numberSylius',
      headerName: 'Sylius',
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Date de commande',
      minWidth: 180,
      flex: 1,
      valueGetter: (value) => moment(value).format('lll'),
    },
    {
      field: 'orderClientState',
      headerName: 'Statut',
      minWidth: 150,
      flex: 1,
      valueGetter: (value: OrderClientState) => OrderClientStateTranslation[value],
      renderCell: ({ value, row }: GridRenderCellParams<OrderClientListByClient, string>) => (
        <>
          {value}{' '}
          {!!row.orderClientLitiges.length && (
            <Chip label="En litige" size="small" color="warning" style={{ height: 20 }} />
          )}
        </>
      ),
    },
    {
      field: 'channel',
      headerName: 'Canal',
      minWidth: 150,
      valueGetter: (value: Channel) => ChannelTranslations[value],
      sortable: false,
    },
    {
      field: 'carrier',
      headerName: 'Transporteur',
      valueGetter: (_, row: OrderClientListByClient) => row.orderClientCarrier?.label,
      sortable: false,
      minWidth: 120,
    },
    {
      field: 'numberCarrier',
      headerName: 'N° Colis',
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'coupon',
      headerName: 'Code Promo',
      valueGetter: (_, row: OrderClientListByClient) => row.orderClientPromotions?.[0]?.coupon,
      sortable: false,
      minWidth: 150,
    },
    {
      field: 'priceTTC',
      headerName: 'Prix final TTC',
      minWidth: 120,
      valueFormatter: (value) => formatNumberToEurCurrency(value),
      sortable: false,
    },
    {
      field: 'guest',
      headerName: 'Guest',
      renderCell: ({ value }) =>
        value ? <FaceRetouchingOff style={{ color: '#ffc107' }} /> : <Face style={{ color: '#28a745' }} />,
      sortable: false,
    },
  ];

  if (
    hasRight(authenticationReducer, 'ROLE_SALE_ORDER_CLIENT') ||
    hasRight(authenticationReducer, 'ROLE_SALE_ORDER_CLIENT_GET')
  ) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      type: 'actions',
      getActions: ({ row }) => [
        <IconAction title="Visualiser" icon="view" to={getRoutePathname('sale_order_client', { uuid: row.uuid })} />,
      ],
    });
  }

  return (
    <DataGrid
      rows={transformObjectToDataGridRowsArray(orderClients)}
      columns={columns}
      rowCount={totalItems}
      {...props}
    />
  );
};

export default OrderClientList;
